/**
 * RDV en ligne (#429) — Story 5.1. Module activation contract (FR1/FR2).
 *
 * The manager turns the "RDV en ligne" module on with a single company toggle
 * (`CompanyOrganizationSetting.rdvOnlineEnabled`), but ONLY when the config is
 * complete and the account is solo. This file holds the pure, testable pieces of
 * that gate:
 *   - the missing-item union (`RdvActivationMissingItem`) surfaced to the FE,
 *   - the status/response + update payload contracts,
 *   - the pure completeness function (`computeRdvActivationMissing`),
 *   - the "≥1 bookable prestation" predicate (`hasBookableServiceDuration`),
 *     which no longer gates activation (#677) but drives public exposure.
 *
 * The completeness predicates MIRROR the planning engine's — same resolution as
 * `resolveSoloMember` / `resolveServiceDuration` (`rdvPlanningEngine.service.ts`).
 * So the BE service maps its rows into `RdvActivationCompletenessInput` and calls
 * this one function rather than re-deriving the rules inline.
 *
 * EXCEPTION (#797): the departure criterion no longer mirrors the engine's
 * `toDepartureLocation` (`rdvPlanningEngine.service.ts`), which stays
 * presence-only — hardening it is a DELIBERATE follow-up, tracked as #889
 * (migrate `toDepartureLocation` to `isReliableGeocode`, plumb the granularity
 * into its own input), not blocked on any missing plumbing here. This gate
 * applies the full `isReliableGeocode` on the departure criterion alone, ahead
 * of that follow-up — not an oversight.
 *
 * FR labels live on the FE: this layer emits only the machine union, never text.
 */
import { z } from "zod";
import { isReliableGeocode } from "../geo/services.js";
/** Payload for `POST /api/rdv-activation/` — flip the module on or off. */
export const UpdateRdvActivationPayloadSchema = z.object({
    enabled: z.boolean(),
});
/**
 * True iff at least one entry is a usable bookable duration (present AND
 * positive) — the "≥1 online-bookable prestation" predicate (#677). The caller
 * supplies the `onlineDurationMinutes` of the company's online-bookable,
 * non-deleted services (same `deleted:false, onlineBookingEnabled:true` filter
 * as `resolveServiceDuration`); a `null` or `<=0` entry is a bookable prestation
 * with no usable duration yet, so it does not count.
 *
 * This is the single source of truth for "bookable" across the two consumers
 * that need it: the activation status (`hasOnlineBookableService` field) and the
 * public-exposure gate (`companyHasBookableService`, BE). It is NOT an
 * activation criterion (#677) — activation no longer depends on it.
 */
export function hasBookableServiceDuration(durations) {
    return durations.some((duration) => duration != null && duration > 0);
}
/**
 * The set of unmet activation criteria (FR1), in the AC's listed order. Empty
 * array ⇒ the config is complete. Solo-ness is NOT checked here — it is a hard
 * block with its own message (`isMultiUser`), resolved by the service from a
 * member count, not from these row-level inputs.
 */
export function computeRdvActivationMissing(input) {
    const missing = [];
    // Departure reliably geocoded — the single criterion (#797), not presence
    // alone: a `municipality` centroid is refused, `null` precision passes (doubt,
    // not a certain negative). Mirrors `assertDepartureGeocoded`
    // (`departureAddress.service.ts`) so the activation gate and the shifting
    // guard never diverge on the same address.
    if (!isReliableGeocode(input.departureGeocodePrecision, input.departureLatitude, input.departureLongitude)) {
        missing.push("departureAddress");
    }
    // ≥ 1 availability slot on any day.
    if (!hasAnyAvailability(input.workingHours)) {
        missing.push("availability");
    }
    // All three travel-time edges chosen (the chip 1.3 writes them together).
    if (input.maxTimeFromHome == null ||
        input.maxTimeBetweenEvents == null ||
        input.maxTimeToHome == null) {
        missing.push("maxTravelTime");
    }
    // NOTE (#677): "≥1 online-bookable prestation" was REMOVED from the activation
    // gate — it now gates PUBLIC EXPOSURE, not activation. See
    // `hasBookableServiceDuration` and the BE `companyHasBookableService`.
    // Company phone present (the no-slot CTA calls it).
    if (input.companyPhone == null || input.companyPhone.trim() === "") {
        missing.push("companyPhone");
    }
    return missing;
}
/** True when at least one day carries at least one working-hours slot. */
function hasAnyAvailability(workingHours) {
    return Object.values(workingHours).some((slots) => Array.isArray(slots) && slots.length > 0);
}
//# sourceMappingURL=activation.js.map